import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToaster } from "../../contexts/ToasterContext";
import { RefreshCw } from "lucide-react";
import { showFreeAssessmentToast } from "../../utils/freeAssessmentRedirect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog.jsx";

// Add custom CSS for shimmer animation
const shimmerStyles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

// Inject styles into document head
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = shimmerStyles;
  document.head.appendChild(styleElement);
}

// DUMMY MOCK DATA - Zero network calls
const generateMockBuckets = () => {
  const now = new Date();
  const nextScanTime = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours from now
  
  return [
    // S3 Buckets
    {
      Id: "s3-bucket-1",
      name: "production-data-bucket",
      provider: "s3",
      StorageType: "S3",
      region: "us-east-1",
      creationDate: "2024-01-15T10:00:00Z",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-1",
        bucketId: "s3-bucket-1",
        bucketName: "production-data-bucket",
        repeatIntervalH: 6,
        startHour: 0,
        active: true,
        nextScheduledAt: nextScanTime.toISOString(),
        provider: "s3",
      },
    },
    {
      Id: "s3-bucket-2",
      name: "staging-backups",
      provider: "s3",
      StorageType: "S3",
      region: "us-west-2",
      creationDate: "2024-02-20T14:30:00Z",
      isScheduled: false,
      hasSchedule: false,
      schedule: null,
    },
    {
      Id: "s3-bucket-3",
      name: "user-uploads-storage",
      provider: "s3",
      StorageType: "S3",
      region: "eu-west-1",
      creationDate: "2024-03-10T09:15:00Z",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-3",
        bucketId: "s3-bucket-3",
        bucketName: "user-uploads-storage",
        repeatIntervalH: 12,
        startHour: 8,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 12 * 60 * 60 * 1000).toISOString(),
        provider: "s3",
      },
    },
    {
      Id: "s3-bucket-4",
      name: "logs-archive-2024",
      provider: "s3",
      StorageType: "S3",
      region: "ap-southeast-1",
      creationDate: "2024-01-05T11:45:00Z",
      isScheduled: false,
      hasSchedule: false,
      schedule: null,
    },
    {
      Id: "s3-bucket-5",
      name: "media-assets-cdn",
      provider: "s3",
      StorageType: "S3",
      region: "us-east-1",
      creationDate: "2024-02-28T16:20:00Z",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-5",
        bucketId: "s3-bucket-5",
        bucketName: "media-assets-cdn",
        repeatIntervalH: 24,
        startHour: 2,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        provider: "s3",
      },
    },
    // GCP Buckets
    {
      Id: "gcp-bucket-1",
      name: "gcp-analytics-data",
      provider: "gcp",
      StorageType: "GCS",
      region: "us-central1",
      creationDate: "2024-01-20T10:00:00Z",
      project: "my-gcp-project",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-gcp-1",
        bucketId: "gcp-bucket-1",
        bucketName: "gcp-analytics-data",
        repeatIntervalH: 3,
        startHour: 0,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString(),
        provider: "gcp",
      },
    },
    {
      Id: "gcp-bucket-2",
      name: "gcp-backup-storage",
      provider: "gcp",
      StorageType: "GCS",
      region: "europe-west1",
      creationDate: "2024-02-15T14:00:00Z",
      project: "my-gcp-project",
      isScheduled: false,
      hasSchedule: false,
      schedule: null,
    },
    {
      Id: "gcp-bucket-3",
      name: "gcp-documents-repo",
      provider: "gcp",
      StorageType: "GCS",
      region: "asia-east1",
      creationDate: "2024-03-05T09:30:00Z",
      project: "my-gcp-project",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-gcp-3",
        bucketId: "gcp-bucket-3",
        bucketName: "gcp-documents-repo",
        repeatIntervalH: 1,
        startHour: 0,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 1 * 60 * 60 * 1000).toISOString(),
        provider: "gcp",
      },
    },
    // Azure Blob Containers
    {
      Id: "azure-container-1",
      name: "azure-app-data",
      provider: "azure",
      StorageType: "AzureBlob",
      region: "eastus",
      creationDate: "2024-01-25T12:00:00Z",
      resourceGroup: "rg-production",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-azure-1",
        bucketId: "azure-container-1",
        bucketName: "azure-app-data",
        repeatIntervalH: 8,
        startHour: 4,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString(),
        provider: "azure",
      },
    },
    {
      Id: "azure-container-2",
      name: "azure-logs-container",
      provider: "azure",
      StorageType: "AzureBlob",
      region: "westus2",
      creationDate: "2024-02-10T08:00:00Z",
      resourceGroup: "rg-staging",
      isScheduled: false,
      hasSchedule: false,
      schedule: null,
    },
    {
      Id: "azure-container-3",
      name: "azure-archives-blob",
      provider: "azure",
      StorageType: "AzureBlob",
      region: "centralus",
      creationDate: "2024-03-01T15:00:00Z",
      resourceGroup: "rg-production",
      isScheduled: true,
      hasSchedule: true,
      schedule: {
        Id: "schedule-azure-3",
        bucketId: "azure-container-3",
        bucketName: "azure-archives-blob",
        repeatIntervalH: 24,
        startHour: 1,
        active: true,
        nextScheduledAt: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        provider: "azure",
      },
    },
  ];
};

const MOCK_BUCKETS = generateMockBuckets();

const MOCK_FILTER_OPTIONS = [
  { storage_type: "s3", count: 5 },
  { storage_type: "gcp", count: 3 },
  { storage_type: "azure", count: 3 },
];



const S3StorageSettings = () => {
  const navigate = useNavigate();
  const { showToast } = useToaster();
  const [activeTab, setActiveTab] = useState("all");

  // Unified State for all storage providers
  const [allBuckets, setAllBuckets] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state - separate for each provider
  const [s3CurrentPage, setS3CurrentPage] = useState(1);
  const [s3ItemsPerPage, setS3ItemsPerPage] = useState(10);
  const [gcsCurrentPage, setGcsCurrentPage] = useState(1);
  const [gcsItemsPerPage, setGcsItemsPerPage] = useState(10);
  const [azureCurrentPage, setAzureCurrentPage] = useState(1);
  const [azureItemsPerPage, setAzureItemsPerPage] = useState(10);
  // Server pagination metadata for each provider
  const [s3Pagination, setS3Pagination] = useState({
    has_next: false,
    has_previous: false,
    last_page: 1,
    next_page: 0,
    page: 1,
    per_page: s3ItemsPerPage,
    prev_page: 0,
    total_count: 0,
  });
  const [gcsPagination, setGcsPagination] = useState({
    has_next: false,
    has_previous: false,
    last_page: 1,
    next_page: 0,
    page: 1,
    per_page: gcsItemsPerPage,
    prev_page: 0,
    total_count: 0,
  });
  const [azurePagination, setAzurePagination] = useState({
    has_next: false,
    has_previous: false,
    last_page: 1,
    next_page: 0,
    page: 1,
    per_page: azureItemsPerPage,
    prev_page: 0,
    total_count: 0,
  });

  const [gcsConfigExists, setGcsConfigExists] = useState(false);
  const [azureConfigExists, setAzureConfigExists] = useState(false);
  const [storageFilterOptions, setStorageFilterOptions] = useState([]);

  // Modal State
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  // Schedule form state
  const [repeatInterval, setRepeatInterval] = useState("1h");
  const [startTime, setStartTime] = useState("hour");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);



  // DUMMY: Check configuration - always return true for all providers
  const checkStorageConfigs = async () => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 100));
    setGcsConfigExists(true);
    setAzureConfigExists(true);
  };

  // Merge buckets with schedule data
  useEffect(() => {
    // If allBuckets already has schedule data (from fetchAllSchedules), use it directly
    if (allBuckets.length > 0 && allBuckets[0].schedule) {
      setMergedData(allBuckets);
      console.debug("Using allBuckets directly as mergedData", {
        count: allBuckets.length,
        sample: allBuckets.map((b) => ({
          id: b.Id,
          name: b.name,
          provider: b.provider,
          hasSchedule: !!b.schedule,
          scheduleActive: b.schedule?.active,
        })),
      });
    } else {
      // Otherwise merge separately fetched buckets and schedules
      const merged = allBuckets.map((bucket) => {
        const bucketIdStr =
          bucket.Id !== undefined && bucket.Id !== null
            ? String(bucket.Id).toLowerCase()
            : "";
        const bucketNameStr =
          bucket.name !== undefined && bucket.name !== null
            ? String(bucket.name).toLowerCase()
            : "";

        const schedule = allSchedules.find((s) => {
          if (!s) return false;
          const sId =
            (s.bucketId || s.BucketId || s.Bucket || s.bucket_id) !==
              undefined &&
            (s.bucketId || s.BucketId || s.Bucket || s.bucket_id) !== null
              ? String(
                  s.bucketId || s.BucketId || s.Bucket || s.bucket_id
                ).toLowerCase()
              : "";
          const sName =
            (s.bucketName || s.BucketName || s.bucket_name) !== undefined &&
            (s.bucketName || s.BucketName || s.bucket_name) !== null
              ? String(
                  s.bucketName || s.BucketName || s.bucket_name
                ).toLowerCase()
              : "";

          const match =
            sId === bucketIdStr ||
            sId === bucketNameStr ||
            sName === bucketIdStr ||
            sName === bucketNameStr;

          return match;
        });

        return {
          ...bucket,
          schedule: schedule || null,
          hasSchedule: !!schedule,
        };
      });

      setMergedData(merged);
      console.debug("Merge effect", {
        bucketsCount: allBuckets.length,
        schedulesCount: allSchedules.length,
        mergedCount: merged.length,
        mergedSample: merged.map((m) => ({
          id: m.Id,
          name: m.name,
          provider: m.provider,
          hasSchedule: m.hasSchedule,
          scheduleActive: m.schedule?.active,
        })),
      });
    }
  }, [allBuckets, allSchedules]);

  useEffect(() => {
    const initializeStorageSettings = async () => {
      // First check what storage configs exist
      await checkStorageConfigs();

      // Fetch all schedules by default
      await fetchAllSchedules();

      // Fetch storage filter options for counts
      await fetchStorageFilterOptions();

      // Set default tab to 'all'
      setActiveTab("all");
    };

    initializeStorageSettings();
    // eslint-disable-next-line
  }, []);

  // DUMMY: Fetch filter options - return mock data
  const fetchStorageFilterOptions = async () => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 100));
    setStorageFilterOptions(MOCK_FILTER_OPTIONS);
  };

  // DUMMY: Fetch all storage schedules - return mock data
  const fetchAllSchedules = async (page = 1, perPage = 50) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate async delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Return paginated mock data
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedBuckets = MOCK_BUCKETS.slice(startIndex, endIndex);
      
      setAllBuckets(paginatedBuckets);
      
      // Set pagination metadata
      const totalCount = MOCK_BUCKETS.length;
      const totalPages = Math.ceil(totalCount / perPage);
      
      setS3Pagination({
        has_next: page < totalPages,
        has_previous: page > 1,
        last_page: totalPages,
        next_page: page < totalPages ? page + 1 : 0,
        page: page,
        per_page: perPage,
        prev_page: page > 1 ? page - 1 : 0,
        total_count: totalCount,
      });
      setS3CurrentPage(page);
    } catch (error) {
      console.error("Error fetching all storage schedules:", error);
      setError(error.message || "Error fetching storage schedules");
    } finally {
      setLoading(false);
    }
  };

  // DUMMY: Fetch provider-specific schedules - return filtered mock data
  const fetchProviderSpecificSchedules = useCallback(
    async (provider, page = 1, perPage = 50) => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate async delay
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Filter mock buckets by provider
        let filteredBuckets = MOCK_BUCKETS;
        if (provider !== "all") {
          filteredBuckets = MOCK_BUCKETS.filter(b => b.provider === provider);
        }
        
        // Paginate
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedBuckets = filteredBuckets.slice(startIndex, endIndex);
        
        setAllBuckets(paginatedBuckets);
        
        // Set pagination metadata
        const totalCount = filteredBuckets.length;
        const totalPages = Math.ceil(totalCount / perPage);
        const normalized = {
          has_next: page < totalPages,
          has_previous: page > 1,
          last_page: totalPages,
          next_page: page < totalPages ? page + 1 : 0,
          page: page,
          per_page: perPage,
          prev_page: page > 1 ? page - 1 : 0,
          total_count: totalCount,
        };
        
        if (provider === "gcp") {
          setGcsPagination(normalized);
          setGcsCurrentPage(normalized.page || page);
        } else if (provider === "azure") {
          setAzurePagination(normalized);
          setAzureCurrentPage(normalized.page || page);
        } else {
          setS3Pagination(normalized);
          setS3CurrentPage(normalized.page || page);
        }
      } catch (error) {
        console.error(`Error fetching ${provider} schedules:`, error);
        setError(error.message || `Error fetching ${provider} schedules`);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch provider-specific schedules when tab changes
  useEffect(() => {
    if (activeTab) {
      // Use provider directly as-is (no mapping from gcp to gcs)
      if (activeTab === "all") {
        fetchAllSchedules(s3CurrentPage, s3ItemsPerPage);
      } else if (activeTab === "s3") {
        fetchProviderSpecificSchedules("s3", s3CurrentPage, s3ItemsPerPage);
      } else if (activeTab === "gcp") {
        fetchProviderSpecificSchedules("gcp", gcsCurrentPage, gcsItemsPerPage);
      } else if (activeTab === "azure") {
        fetchProviderSpecificSchedules(
          "azure",
          azureCurrentPage,
          azureItemsPerPage
        );
      } else {
        fetchProviderSpecificSchedules(
          activeTab,
          s3CurrentPage,
          s3ItemsPerPage
        );
      }
    }
  }, [
    activeTab,
    fetchProviderSpecificSchedules,
    s3CurrentPage,
    s3ItemsPerPage,
    gcsCurrentPage,
    gcsItemsPerPage,
    azureCurrentPage,
    azureItemsPerPage,
  ]);

  // Get available tabs - always show all three providers
  const getAvailableTabs = () => {
    return [
      { id: "s3", label: "S3", logo: "/inte/AWSS3.svg" },
      { id: "azure", label: "Azure Blob", logo: "/inte/AzureBlockStorage.png" },
      { id: "gcp", label: "GCP", logo: "/inte/GoogleCloudStorage.svg" },
    ];
  };

  const availableTabs = getAvailableTabs();

  // Update active tab if current tab is no longer available
  useEffect(() => {
    const availableTabIds = ["all", ...availableTabs.map((tab) => tab.id)];
    if (!availableTabIds.includes(activeTab)) {
      setActiveTab(availableTabIds[0] || "all");
    }
  }, [availableTabs, activeTab]);



  // DUMMY: Search storage scan schedules - filter mock data by query
  const searchStorageSchedules = async (
    query,
    page = 1,
    perPage = 50,
    sortBy = null,
    sortOrder = null
  ) => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Filter mock buckets by name (case-insensitive)
    const queryLower = query.toLowerCase();
    const filtered = MOCK_BUCKETS.filter(bucket =>
      bucket.name.toLowerCase().includes(queryLower)
    );
    
    return filtered;
  };

  // Handle search - auto-triggers on query change
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchStorageSchedules(query, 1, 50, null, null);

      console.log("Raw search results from API:", results);
      console.log("Results length:", results.length);

      if (!results || results.length === 0) {
        console.warn("No search results returned from API");
        setSearchResults([]);
        return;
      }

      // Transform results to bucket format matching renderStorageTable expectations
      const transformedBuckets = results.map((item, index) => {
        console.log(`Processing search result ${index}:`, item);

        let provider = "s3";
        const storageType = (
          item.StorageType ||
          item.storage_type ||
          item.Provider ||
          item.provider ||
          ""
        )
          .toString()
          .toLowerCase();

        if (storageType.includes("azure") || storageType.includes("blob")) {
          provider = "azure";
        } else if (
          storageType.includes("gcs") ||
          storageType.includes("google") ||
          storageType.includes("gcp")
        ) {
          provider = "gcp";
        }

        const bucketName =
          item.BucketName ||
          item.bucketName ||
          item.name ||
          item.bucket_name ||
          `Unknown-${index}`;
        const bucketId =
          item.BucketId || item.bucketId || item.id || `search-${index}`;

        return {
          Id: bucketId,
          name: bucketName,
          provider: provider,
          StorageType:
            item.StorageType || item.storage_type || storageType || "Unknown",
          region: item.region || "N/A",
          creationDate: item.CreatedAt || item.createdAt || null,
          isScheduled: item.Active || item.active || false,
          hasSchedule: !!(item.Id || item.id),
          schedule: {
            Id: item.Id || item.id,
            bucketId: bucketId,
            bucketName: bucketName,
            repeatIntervalH: item.RepeatIntervalM
              ? item.RepeatIntervalM / 60
              : item.repeatIntervalH || 1,
            startHour: item.StartHour || item.startHour || 0,
            active: item.Active || item.active || false,
            nextScheduledAt: item.NextScheduledAt || item.next_scheduled_at,
            provider: provider,
          },
        };
      });

      setSearchResults(transformedBuckets);
      console.log("Transformed search results:", transformedBuckets);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Auto-trigger search when query changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery);
      } else {
        setSearchResults(null);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch]);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults(null);
  }, []);

  // DUMMY: Fetch schedules - return mock schedule data
  const fetchSchedules = async (
    sortBy = "next_scheduled_at",
    sortOrder = "asc"
  ) => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Extract schedules from mock buckets
    const schedules = MOCK_BUCKETS
      .filter(b => b.schedule)
      .map(b => ({
        id: b.schedule.Id,
        Id: b.schedule.Id,
        bucketId: b.schedule.bucketId,
        bucketName: b.schedule.bucketName,
        startHour: b.schedule.startHour,
        repeatIntervalH: b.schedule.repeatIntervalH,
        active: b.schedule.active,
        timezone: "UTC +00:00",
        repeatInterval: `${b.schedule.repeatIntervalH}h`,
        startTime: b.schedule.startHour === 0 ? "now" : "hour",
        provider: b.provider,
        createdAt: b.creationDate,
      }));
    
    setAllSchedules(schedules);
  };

  // Helper function to convert repeat interval to hours
  const convertIntervalToMinutes = (interval) => {
    const match = interval.match(/^(\d+)([mhdw])$/);
    if (!match) return 60; // Default to 60 minutes (1 hour)

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
      case "m":
        return value; // minutes
      case "h":
        return value * 60; // hours to minutes
      case "d":
        return value * 24 * 60; // days to minutes
      case "w":
        return value * 24 * 7 * 60; // weeks to minutes
      default:
        return 60;
    }
  };



  // Convert to ISO format for startHour/startTime
  const getISOStartTime = (startTimeOption) => {
    if (startTimeOption === "now") {
      return new Date().toISOString();
    }

    const now = new Date();
    let targetDate = new Date(now);

    // Set to current hour
    targetDate.setMinutes(0);
    targetDate.setSeconds(0);
    targetDate.setMilliseconds(0);

    return targetDate.toISOString();
  };

  // Helper function to get timezone display name
  const getNextScheduleTime = (schedule) => {
    if (!schedule) return "N/A";

    // If nextScheduledAt is available from backend, use it directly
    if (schedule.nextScheduledAt) {
      try {
        const nextScanDate = new Date(schedule.nextScheduledAt);
        if (!isNaN(nextScanDate.getTime())) {
          return nextScanDate.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      } catch (error) {
        console.error("Error parsing nextScheduledAt:", error);
      }
    }

    // Fallback: calculate based on startHour and repeatIntervalH
    const now = new Date();
    const startHour = schedule.startHour || 0;
    const repeatIntervalH = schedule.repeatIntervalH || 1;

    let nextScanTime = new Date(now);
    nextScanTime.setHours(startHour, 0, 0, 0);

    while (nextScanTime <= now) {
      nextScanTime.setTime(
        nextScanTime.getTime() + repeatIntervalH * 60 * 60 * 1000
      );
    }

    return nextScanTime.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Handle schedule actions
  const handleCreateSchedule = async () => {
    if (!selectedBucket) return;

    try {
      setScheduleLoading(true);
      const scheduleData = {
        startHour: getISOStartTime(startTime),
        repeatIntervalM: convertIntervalToMinutes(repeatInterval),
        bucketId: selectedBucket.Id,
        ...(selectedBucket.accountId && {
          accountId: selectedBucket.accountId,
        }),
      };

      const result = await createSchedule(scheduleData);

      if (result.Id || result.id || result.statusCode === 201) {
        showToast("Schedule created successfully!", "success");
        setShowScheduleModal(false);
        setSelectedBucket(null);
      }
    } catch (err) {
      const errorMessage =
        err?.message || "Failed to create schedule. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule) return;

    try {
      setScheduleLoading(true);
      const scheduleId = editingSchedule.Id || editingSchedule.id;

      if (!scheduleId) {
        showToast("Error: Schedule ID is missing", "error");
        return;
      }

      const updateData = {
        startTime: getISOStartTime(startTime),
        repeatIntervalM: convertIntervalToMinutes(repeatInterval),
        bucketId: editingSchedule.bucketId,
        active:
          editingSchedule.active !== undefined ? editingSchedule.active : true,
        ...(editingSchedule.accountId && {
          accountId: editingSchedule.accountId,
        }),
      };

      await updateSchedule(scheduleId, updateData);

      // Update the merged data immediately with the new schedule info
      const repeatIntervalH = convertIntervalToMinutes(repeatInterval) / 60;
      const updatedSchedule = {
        ...editingSchedule,
        repeatInterval: repeatInterval,
        repeatIntervalH: repeatIntervalH,
        startTime: startTime,
      };

      // Update mergedData with the new schedule
      setMergedData((prevData) =>
        prevData.map((bucket) =>
          bucket.Id === editingSchedule.bucketId ||
          bucket.id === editingSchedule.bucketId
            ? { ...bucket, schedule: updatedSchedule }
            : bucket
        )
      );

      showToast("Schedule updated successfully!", "success");
      setShowScheduleModal(false);
      setEditingSchedule(null);
    } catch (error) {
      const errorMessage = error?.message || "Failed to update schedule";
      showToast(errorMessage, "error");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleDeleteSchedule = (schedule) => {
    if (!schedule?.id) return;
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete?.id) return;

    try {
      await deleteSchedule(scheduleToDelete.id);
      showToast("Schedule deleted successfully!", "success");
    } catch (error) {
      const errorMessage = error?.message || "Failed to delete schedule";
      showToast(errorMessage, "error");
    } finally {
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    }
  };

  // Modal handlers
  const openScheduleModal = (bucket, schedule = null) => {
    setSelectedBucket(bucket);
    setEditingSchedule(schedule);

    if (schedule) {
      setRepeatInterval(schedule.repeatInterval || "1h");
      setStartTime(schedule.startTime || "hour");
    } else {
      setRepeatInterval("1h");
      setStartTime("hour");
    }

    setShowScheduleModal(true);
  };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedBucket(null);
    setEditingSchedule(null);
  };

  // Helper to get provider from bucket or schedule
  const getProviderFromBucket = (bucket) => {
    if (!bucket) return "s3";
    const providerRaw = (
      bucket.provider ||
      bucket.storage_provider ||
      bucket.storageProvider ||
      bucket.provider_name ||
      ""
    )
      .toString()
      .toLowerCase();
    if (
      providerRaw.includes("gcs") ||
      providerRaw.includes("google") ||
      providerRaw.includes("gcp")
    ) {
      return "gcp";
    } else if (providerRaw.includes("azure") || providerRaw.includes("blob")) {
      return "azure";
    }
    return "s3";
  };

  // Helper to get storage provider logo
  const getStorageProviderLogo = (provider) => {
    if (!provider) return null;

    const providerLower = provider.toLowerCase().trim();
    const logoMap = {
      s3: "/inte/AWSS3.svg",
      azure: "/inte/AzureBlockStorage.png",
      gcp: "/inte/GoogleCloudStorage.svg",
    };

    return logoMap[providerLower] || null;
  };

  // DUMMY: Schedule CRUD operations - simulate with state updates
  const createSchedule = async (scheduleData, provider = null) => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Update state with new schedule
    setAllBuckets(prevBuckets => {
      return prevBuckets.map(bucket => {
        if (bucket.Id === scheduleData.bucketId) {
          const newScheduleId = `schedule-${Date.now()}`;
          return {
            ...bucket,
            schedule: {
              Id: newScheduleId,
              bucketId: scheduleData.bucketId,
              bucketName: bucket.name,
              repeatIntervalH: scheduleData.repeatIntervalM / 60,
              startHour: 0,
              active: true,
              nextScheduledAt: new Date(Date.now() + scheduleData.repeatIntervalM * 60 * 1000).toISOString(),
              provider: bucket.provider,
            },
            hasSchedule: true,
            isScheduled: true,
          };
        }
        return bucket;
      });
    });
    
    // Also update mergedData
    setMergedData(prevData => {
      return prevData.map(bucket => {
        if (bucket.Id === scheduleData.bucketId) {
          const newScheduleId = `schedule-${Date.now()}`;
          return {
            ...bucket,
            schedule: {
              Id: newScheduleId,
              bucketId: scheduleData.bucketId,
              bucketName: bucket.name,
              repeatIntervalH: scheduleData.repeatIntervalM / 60,
              startHour: 0,
              active: true,
              nextScheduledAt: new Date(Date.now() + scheduleData.repeatIntervalM * 60 * 1000).toISOString(),
              provider: bucket.provider,
            },
            hasSchedule: true,
            isScheduled: true,
          };
        }
        return bucket;
      });
    });
    
    return { Id: `schedule-${Date.now()}`, statusCode: 201 };
  };

  const updateSchedule = async (scheduleId, scheduleData, provider = null) => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Update state
    setAllBuckets(prevBuckets => {
      return prevBuckets.map(bucket => {
        if (bucket.schedule && (bucket.schedule.Id === scheduleId || bucket.schedule.id === scheduleId)) {
          return {
            ...bucket,
            schedule: {
              ...bucket.schedule,
              repeatIntervalH: scheduleData.repeatIntervalM / 60,
              active: scheduleData.active !== undefined ? scheduleData.active : true,
              nextScheduledAt: new Date(Date.now() + scheduleData.repeatIntervalM * 60 * 1000).toISOString(),
            },
          };
        }
        return bucket;
      });
    });
    
    // Also update mergedData
    setMergedData(prevData => {
      return prevData.map(bucket => {
        if (bucket.schedule && (bucket.schedule.Id === scheduleId || bucket.schedule.id === scheduleId)) {
          return {
            ...bucket,
            schedule: {
              ...bucket.schedule,
              repeatIntervalH: scheduleData.repeatIntervalM / 60,
              active: scheduleData.active !== undefined ? scheduleData.active : true,
              nextScheduledAt: new Date(Date.now() + scheduleData.repeatIntervalM * 60 * 1000).toISOString(),
            },
          };
        }
        return bucket;
      });
    });
    
    return { Id: scheduleId };
  };

  const deleteSchedule = async (scheduleId, provider = null) => {
    // Simulate async delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Update state
    setAllBuckets(prevBuckets => {
      return prevBuckets.map(bucket => {
        if (bucket.schedule && (bucket.schedule.Id === scheduleId || bucket.schedule.id === scheduleId)) {
          return {
            ...bucket,
            schedule: null,
            hasSchedule: false,
            isScheduled: false,
          };
        }
        return bucket;
      });
    });
    
    // Also update mergedData
    setMergedData(prevData => {
      return prevData.map(bucket => {
        if (bucket.schedule && (bucket.schedule.Id === scheduleId || bucket.schedule.id === scheduleId)) {
          return {
            ...bucket,
            schedule: null,
            hasSchedule: false,
            isScheduled: false,
          };
        }
        return bucket;
      });
    });
    
    return { success: true };
  };

  // Shimmer component for loading state
  const S3TableShimmer = ({ rows = 5 }) => (
    <div className="bg-white border border-gray-300 rounded-xl overflow-hidden min-h-[400px]">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="p-3 text-left text-sm font-semibold text-gray-600">
              Bucket Name
            </th>
            <th className="p-3 text-left text-sm font-semibold text-gray-600">
              Schedule Status
            </th>
            <th className="p-3 text-left text-sm font-semibold text-gray-600">
              Current Interval
            </th>
            <th className="p-3 text-left text-sm font-semibold text-gray-600">
              Next Scan
            </th>
            <th className="p-3 text-left text-sm font-semibold text-gray-600">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, index) => (
            <tr key={index} className="animate-pulse">
              <td className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </td>
              <td className="p-3">
                <div className="h-6 bg-gray-200 rounded-full w-24"></div>
              </td>
              <td className="p-3">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </td>
              <td className="p-3">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </td>
              <td className="p-3">
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Error state component
  const ErrorState = ({ error, onRetry }) => (
    <div className="bg-white border border-red-300 rounded-xl p-6">
      <div className="flex items-center gap-2 text-red-600 mb-4">
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-red-600 text-sm mb-4">{error}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-[#C0ACF3] text-black rounded hover:bg-[#a088ea] border border-[#C0ACF3] font-medium text-sm"
      >
        Retry
      </button>
    </div>
  );

  // Helper function to render storage table
  const renderStorageTable = (
    data,
    loading,
    error,
    fetchFunction,
    storageType,
    configExists = true,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage
  ) => {
    const headerMap = {
      all: "Bucket Name",
      s3: "Bucket Name",
      gcp: "Bucket Name",
      azure: "Container Name",
    };

    // Ensure data is always an array
    const dataArray = Array.isArray(data) ? data : [];

    if (!configExists) {
      return (
        <div className="bg-white border border-gray-300 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {storageType === "gcp"
              ? "Google Cloud Storage"
              : storageType === "azure"
              ? "Azure Blob Storage"
              : "S3 Storage"}{" "}
            Not Configured
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Please configure{" "}
            {storageType === "gcp"
              ? "GCS"
              : storageType === "azure"
              ? "Azure Blob"
              : "S3"}{" "}
            credentials in the integrations page to view{" "}
            {storageType === "azure" ? "containers" : "buckets"}.
          </p>
          <button className="px-4 py-2 bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a9a] transition-all duration-200 font-medium">
            Configure{" "}
            {storageType === "gcp"
              ? "GCS"
              : storageType === "azure"
              ? "Azure"
              : "S3"}
          </button>
        </div>
      );
    }

    if (loading) {
      return <S3TableShimmer />;
    }

    if (error) {
      return <ErrorState error={error} onRetry={fetchFunction} />;
    }

    if (dataArray.length === 0) {
      return (
        <div className="bg-white border border-gray-300 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {storageType === "azure" ? "Containers" : "Buckets"} Found
          </h3>
          <p className="text-gray-600 text-sm">
            No {storageType === "azure" ? "containers" : "buckets"} were found
            in your{" "}
            {storageType === "gcp"
              ? "GCP"
              : storageType === "azure"
              ? "Azure"
              : "AWS"}{" "}
            account.
          </p>
        </div>
      );
    }

    // Calculate pagination - prefer server-provided metadata when available
    const totalItems = dataArray.length;
    let paginationMeta = null;
    if (storageType === "gcp") paginationMeta = gcsPagination;
    else if (storageType === "azure") paginationMeta = azurePagination;
    else paginationMeta = s3Pagination;

    const totalPages =
      paginationMeta && paginationMeta.last_page && paginationMeta.last_page > 0
        ? paginationMeta.last_page
        : Math.max(1, Math.ceil(totalItems / itemsPerPage));

    // Data from server is already paginated, don't slice it again
    // Only slice if we're doing client-side pagination (i.e., when all data is loaded locally)
    const isServerPaginated =
      paginationMeta &&
      paginationMeta.last_page &&
      paginationMeta.last_page > 0;
    let paginatedData = dataArray;

    if (!isServerPaginated) {
      // Client-side pagination: slice the data
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      paginatedData = dataArray.slice(startIndex, endIndex);
    }



    return (
      <div className="h-full flex flex-col min-h-0 gap-4">
        <div className="flex-1 min-h-0 bg-white border border-gray-300 rounded-xl overflow-hidden shadow-sm relative">
          <div className="absolute inset-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
            <table className="min-w-full relative">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="p-3 text-left text-sm font-semibold text-gray-600">
                    {headerMap[storageType]}
                  </th>
                  {/* Provider column removed */}
                  <th className="p-3 text-left text-sm font-semibold text-gray-600">
                    Schedule Status
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-600">
                    Current Interval
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-600">
                    Next Scan
                  </th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((bucket) => {
                  // Inline renderRow logic with Provider column removed
                  const schedule = bucket.schedule || null;
                  const hasSchedule = !!schedule;
                  return (
                    <tr
                      key={bucket.Id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center">
                            {getStorageProviderLogo(bucket.provider) ? (
                              <img
                                src={getStorageProviderLogo(bucket.provider)}
                                alt={bucket.provider || "Unknown"}
                                className="h-6 w-auto max-w-[28px]"
                                title={
                                  bucket.provider
                                    ? bucket.provider.toUpperCase()
                                    : "Unknown"
                                }
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <span className="text-gray-400 font-bold text-lg">
                                ?
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900 truncate max-w-[200px] sm:max-w-[300px] md:max-w-none block">
                              {bucket.name}
                            </span>
                            {storageType === "gcp" && bucket.project && (
                              <span className="text-xs text-gray-500">
                                {bucket.project}
                              </span>
                            )}
                            {storageType === "azure" &&
                              bucket.resourceGroup && (
                                <span className="text-xs text-gray-500">
                                  {bucket.resourceGroup}
                                </span>
                              )}
                          </div>
                        </div>
                      </td>
                      {/* Provider column removed */}
                      <td className="p-3">
                        {schedule && schedule.active === true ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Scheduled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Not Scheduled
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {schedule ? (
                          <span className="font-medium">
                            Every{" "}
                            {typeof schedule.repeatIntervalH === "number"
                              ? schedule.repeatIntervalH < 1
                                ? `${Math.round(
                                    schedule.repeatIntervalH * 60
                                  )}m`
                                : Number.isInteger(schedule.repeatIntervalH)
                                ? `${schedule.repeatIntervalH}h`
                                : `${schedule.repeatIntervalH.toFixed(2)}h`
                              : `${schedule.repeatIntervalH || 1}h`}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {hasSchedule ? (
                          <span className="font-medium">
                            {getNextScheduleTime(schedule)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {hasSchedule ? (
                            <>
                              <button
                                onClick={() =>
                                  openScheduleModal(bucket, schedule)
                                }
                                className="p-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                title="Edit schedule"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteSchedule(schedule)}
                                className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                title="Delete schedule"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => openScheduleModal(bucket)}
                              className="px-3 py-1.5 text-sm bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a9a] transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center gap-1"
                            >
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                              </svg>
                              Schedule
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex-none flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const target = Math.max(1, currentPage - 1);
                try {
                  if (typeof fetchFunction === "function")
                    fetchFunction(target, itemsPerPage);
                } catch (_) {}
                setCurrentPage(target);
              }}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
              }`}
            >
              Previous
            </button>

            {/* Numeric pagination buttons (show pages around current) */}
            <div className="inline-flex items-center gap-1">
              {(() => {
                const buttons = [];
                const page = currentPage;

                // Always show the first page
                buttons.push(
                  <button
                    key={1}
                    onClick={() => {
                      try {
                        if (typeof fetchFunction === "function")
                          fetchFunction(1, itemsPerPage);
                      } catch (_) {}
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      page === 1
                        ? "bg-[#44087D] text-white"
                        : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
                    }`}
                  >
                    1
                  </button>
                );

                // Show ellipsis if current page is far from the first page
                if (page > 3) {
                  buttons.push(
                    <span
                      key="ellipsis-start"
                      className="px-3 py-2 text-sm text-gray-600"
                    >
                      ...
                    </span>
                  );
                }

                // Show pages around the current page
                for (
                  let i = Math.max(2, page - 1);
                  i <= Math.min(totalPages - 1, page + 1);
                  i++
                ) {
                  buttons.push(
                    <button
                      key={i}
                      onClick={() => {
                        try {
                          if (typeof fetchFunction === "function")
                            fetchFunction(i, itemsPerPage);
                        } catch (_) {}
                        setCurrentPage(i);
                      }}
                      className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                        page === i
                          ? "bg-[#44087D] text-white"
                          : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }

                // Show ellipsis if current page is far from the last page
                if (page < totalPages - 2) {
                  buttons.push(
                    <span
                      key="ellipsis-end"
                      className="px-3 py-2 text-sm text-gray-600"
                    >
                      ...
                    </span>
                  );
                }

                // Always show the last page
                if (totalPages > 1) {
                  buttons.push(
                    <button
                      key={totalPages}
                      onClick={() => {
                        try {
                          if (typeof fetchFunction === "function")
                            fetchFunction(totalPages, itemsPerPage);
                        } catch (_) {}
                        setCurrentPage(totalPages);
                      }}
                      className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                        page === totalPages
                          ? "bg-[#44087D] text-white"
                          : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return buttons;
              })()}
            </div>

            <button
              onClick={() => {
                const target = Math.min(totalPages, currentPage + 1);
                try {
                  if (typeof fetchFunction === "function")
                    fetchFunction(target, itemsPerPage);
                } catch (_) {}
                setCurrentPage(target);
              }}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
              }`}
            >
              Next
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const v = Number(e.target.value);
                try {
                  if (typeof fetchFunction === "function") fetchFunction(1, v);
                } catch (_) {}
                setItemsPerPage(v);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              className="px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#44087D] focus:outline-none"
            >
              {[2, 5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Action Bar / Search */}
      <div className="flex-none flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="relative flex-1 w-full md:max-w-md">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search storage schedules by bucket name..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-[#44087D] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            showFreeAssessmentToast();
          }}
          className="px-4 py-2 bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a9a] transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center gap-2 whitespace-nowrap"
        >
          <RefreshCw className="w-4 h-4" />
          New Scan
        </button>
      </div>

      {/* Display search results if searching */}
      {searchResults !== null ? (
        <div className="flex-1 min-h-0 flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Search Results for "{searchQuery}" ({searchResults.length})
            </h3>
            <button
              onClick={handleClearSearch}
              className="text-sm text-[#44087D] hover:text-[#5a0a9a] font-medium"
            >
              Clear Search
            </button>
          </div>
          {renderStorageTable(
            searchResults,
            false,
            null,
            () => handleSearch({ preventDefault: () => {} }),
            "all",
            true,
            s3CurrentPage,
            setS3CurrentPage,
            s3ItemsPerPage,
            setS3ItemsPerPage
          )}
        </div>
      ) : (
        <>
          {/* Storage Provider Tabs */}
          <div className="flex-none mb-6">
            <div className="flex flex-wrap gap-2 items-center">
              {/* All Badge */}
              <button
                onClick={() => setActiveTab("all")}
                className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  activeTab === "all"
                    ? "bg-[#44087D] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === "all"
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {storageFilterOptions.reduce(
                    (sum, opt) => sum + opt.count,
                    0
                  )}
                </span>
              </button>

              {/* Individual Provider Tabs - no images, no extra space */}
              {availableTabs.map((tab) => {
                const option = storageFilterOptions.find(
                  (opt) => opt.storage_type === tab.id
                );
                const count = option ? option.count : 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 capitalize ${
                      activeTab === tab.id
                        ? "bg-[#44087D] text-white"
                        : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
                    }`}
                  >
                    {tab.logo && (
                      <img
                        src={tab.logo}
                        alt={tab.label}
                        className="w-5 h-5 mr-2 object-contain"
                      />
                    )}
                    {tab.label}
                    <span
                      className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        activeTab === tab.id
                          ? "bg-white/20 text-white"
                          : "bg-[#E0D9F0] text-[#5D5275]"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === "all" ? (
            <div className="flex-1 min-h-0 h-full">
              {renderStorageTable(
                mergedData,
                loading,
                error,
                fetchAllSchedules,
                "all",
                true,
                s3CurrentPage,
                setS3CurrentPage,
                s3ItemsPerPage,
                setS3ItemsPerPage
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 h-full">
              {activeTab === "s3" && (
                <div className="h-full">
                  {renderStorageTable(
                    mergedData.filter((b) => b.provider === "s3"),
                    loading,
                    error,
                    fetchAllSchedules,
                    "s3",
                    true,
                    s3CurrentPage,
                    setS3CurrentPage,
                    s3ItemsPerPage,
                    setS3ItemsPerPage
                  )}
                </div>
              )}

              {activeTab === "gcp" && (
                <div className="h-full">
                  {renderStorageTable(
                    mergedData.filter((b) => b.provider === "gcp"),
                    loading,
                    error,
                    fetchAllSchedules,
                    "gcp",
                    true,
                    gcsCurrentPage,
                    setGcsCurrentPage,
                    gcsItemsPerPage,
                    setGcsItemsPerPage
                  )}
                </div>
              )}

              {activeTab === "azure" && (
                <div className="h-full">
                  {renderStorageTable(
                    mergedData.filter((b) => b.provider === "azure"),
                    loading,
                    error,
                    fetchAllSchedules,
                    "azure",
                    true,
                    azureCurrentPage,
                    setAzureCurrentPage,
                    azureItemsPerPage,
                    setAzureItemsPerPage
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Schedule Configuration Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingSchedule ? "Edit Schedule" : "Create Schedule"}
              </h3>
              <button
                onClick={closeScheduleModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {selectedBucket && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#44087D] rounded flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900">
                    {selectedBucket.name}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="startTime"
                      value="now"
                      checked={startTime === "now"}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-[#6D49FE] bg-gray-100 border-gray-300 focus:ring-[#6D49FE] focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Start Now</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="startTime"
                      value="hour"
                      checked={startTime === "hour"}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-[#6D49FE] bg-gray-100 border-gray-300 focus:ring-[#6D49FE] focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Current Hour</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repeat Interval
                </label>
                <select
                  value={repeatInterval}
                  onChange={(e) => setRepeatInterval(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6D49FE] focus:border-transparent"
                >
                  <option value="1h">Every 1 hour</option>
                  <option value="3h">Every 3 hours</option>
                  <option value="6h">Every 6 hours</option>
                  <option value="12h">Every 12 hours</option>
                  <option value="1d">Every 1 day</option>
                  <option value="3d">Every 3 days</option>
                  <option value="1w">Every 1 week</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={
                  editingSchedule ? handleUpdateSchedule : handleCreateSchedule
                }
                disabled={scheduleLoading}
                className="flex-1 px-4 py-3 bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a9a] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                {scheduleLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {editingSchedule ? "Updating..." : "Creating..."}
                  </div>
                ) : editingSchedule ? (
                  "Update Schedule"
                ) : (
                  "Create Schedule"
                )}
              </button>
              <button
                onClick={closeScheduleModal}
                disabled={scheduleLoading}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the schedule for{" "}
              <span className="font-semibold">
                {scheduleToDelete?.bucketName || "this bucket"}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSchedule}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default S3StorageSettings;
